import PageHead from "../components/PageHead";
import CTA from "../components/CTA";

const TILES = [
  ["Tenant data isolation", "Documents, extracted data, models and audit history are scoped to a single tenant. No organisation can ever see another's work.",
    <svg key="ic" className="tile__ic" viewBox="0 0 22 22" fill="none"><rect x="3" y="3" width="7" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.6"/><rect x="12" y="3" width="7" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.6"/><rect x="3" y="12" width="7" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.6"/><rect x="12" y="12" width="7" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.6"/></svg>],
  ["Encryption in transit & at rest", "Documents and data are encrypted on the wire and in storage. Sensitive material is never left exposed at any stage of the pipeline.",
    <svg key="ic" className="tile__ic" viewBox="0 0 22 22" fill="none"><rect x="4" y="9" width="14" height="10" rx="1.6" stroke="currentColor" strokeWidth="1.6"/><path d="M7.5 9V6.5a3.5 3.5 0 017 0V9" stroke="currentColor" strokeWidth="1.6"/><circle cx="11" cy="14" r="1.4" fill="currentColor"/></svg>],
  ["Role-based access at the API", "Permissions are enforced server-side, at the API layer. There is no client-side bypass, the interface only ever reflects what the role allows.",
    <svg key="ic" className="tile__ic" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="8" r="3" stroke="currentColor" strokeWidth="1.6"/><path d="M5 18c0-3 2.7-4.8 6-4.8s6 1.8 6 4.8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M14.5 6.5l1.5 1.5 2.5-2.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>],
  ["Per-module access control", "Access is assigned at invite and enforced per session. People see only the modules, training, extraction, reports, settings, their role requires.",
    <svg key="ic" className="tile__ic" viewBox="0 0 22 22" fill="none"><path d="M11 2.5l7 2.5v5c0 5-3 8.5-7 9.5C7 18.5 4 15 4 10V5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/><path d="M8 10.5l2 2 4-4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>],
  ["Tamper-evident audit trail", "Every action, and every destructive one in particular, is logged with actor, timestamp and before/after state. Compliance becomes a by-product.",
    <svg key="ic" className="tile__ic" viewBox="0 0 22 22" fill="none"><path d="M5 3.5h9l4 4v11H5z" stroke="currentColor" strokeWidth="1.6"/><path d="M14 3.5v4h4" stroke="currentColor" strokeWidth="1.6"/><path d="M8 11.5l1.8 1.8L13 9.8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>],
  ["PII anonymisation on export", "Personal data can be anonymised when audit data is exported, for regulated deployments where information leaves the platform.",
    <svg key="ic" className="tile__ic" viewBox="0 0 22 22" fill="none"><circle cx="11" cy="8.5" r="3.2" stroke="currentColor" strokeWidth="1.6"/><path d="M5 18c0-3.3 2.7-5.3 6-5.3s6 2 6 5.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/><path d="M9 8.3l1.4 1.4 2.4-2.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>],
];

export default function Security() {
  return (
    <>
      <PageHead
        crumb="Security"
        eyebrow="Security & architecture"
        title="Built multi-tenant. Built to be trusted."
        lede="Docket processes some of the most sensitive documents a business holds, invoices, contracts, claims, identity forms. Security is not a settings page you visit once; it is part of how the platform is built."
      />

      <section className="section">
        <div className="container">
          <div className="s-head reveal">
            <div className="eyebrow">Multi-tenancy</div>
            <h2>Every organisation is an isolated tenant.</h2>
            <p className="lede">A tenant is a sealed environment, its own users, models, configuration, storage and document data. Nothing crosses the boundary between one tenant and another.</p>
          </div>
          <div className="tenants reveal">
            {["Acme Logistics", "Northwind Finance", "Meridian Health"].map((t) => (
              <div key={t} className="tnt">
                <div className="tnt__lab">Tenant, {t}</div>
                <div className="tnt__rows"><i /><i /><i /></div>
                <div className="tnt__wall">· sealed boundary ·</div>
              </div>
            ))}
          </div>
          <p className="reveal" style={{ marginTop: 26, color: "var(--ink-soft)", maxWidth: "60ch" }}>
            Each tenant is administered independently, its own users, its own trained models, its own thresholds and retention rules. A tenant can be added without re-architecting anything, because isolation is the default, not an add-on.
          </p>
        </div>
      </section>

      <section className="section bg-2 bordered">
        <div className="container">
          <div className="s-head reveal" style={{ marginBottom: 48 }}>
            <div className="eyebrow">The controls</div>
            <h2>Six things that are true on every deployment.</h2>
          </div>
          <div className="tilegrid reveal">
            {TILES.map(([h, p, ic]) => (
              <div key={h} className="tile">{ic}<h3>{h}</h3><p>{p}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="split2">
            <div className="lead-block reveal">
              <div className="eyebrow" style={{ marginBottom: 22 }}>Architecture</div>
              <h2>Cloud-native, and built to grow without re-architecting.</h2>
              <p>Docket is built as a modern, cloud-native platform, so capacity, tenants and models scale with demand.</p>
              <p className="mark">Adding a tenant, a model or a team is a configuration change, never an engineering project.</p>
            </div>
            <div className="plist">
              {[
                ["Configuration over code", "Tenants, models, thresholds, retention and access are all managed through the Super Admin and Settings modules, not by deploying changes."],
                ["Designed to integrate", "Structured output and direct connections let Docket sit cleanly alongside the DMS and systems of record an organisation already runs."],
                ["Scales with the work", "Document volume can grow sharply without the platform needing to be re-architected to keep pace."],
              ].map(([h, p], i) => (
                <div key={h} className={`pitem reveal${i ? ` d${i}` : ""}`}>
                  <div className="pitem__no">→</div>
                  <div><h3>{h}</h3><p>{p}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <CTA
        title='Bring your security questions to a <em>pilot</em>.'
        text="A pilot is the right place to walk through tenancy, access control and audit in the context of your own deployment requirements."
        secondary={{ to: "/how-it-works", label: "See how it works" }}
      />
    </>
  );
}
